import React, { useMemo } from 'react';
import { Alert, Card, Col, Row, Space, Statistic, Table, Tag, Typography } from 'antd';
import { formatMoney } from '@/utils/format';
import { EST3_TIME_RELATED_INDIRECT_METHODS } from './est3ProjectCostBuckets';
import { EpcEstimateProjectCostStrip } from './EpcEstimateProjectCostStrip';
import { Wave5DurationPanel, useWave5Queries, type Wave5Ctx } from './EpcEstimateWave5Panels';

const { Text } = Typography;

export interface EpcEstimateTimeRelatedWorkbenchProps {
  ctx: Wave5Ctx;
}

/**
 * EST3 UX2 Wave 7 — Time-related cost: duration + duration-linked indirect lines.
 */
export const EpcEstimateTimeRelatedWorkbench: React.FC<EpcEstimateTimeRelatedWorkbenchProps> = ({
  ctx,
}) => {
  const { durationQ, indirectQ, totalQ } = useWave5Queries(ctx);

  const timeRelatedLines = useMemo(
    () =>
      (indirectQ.data || []).filter((row: any) =>
        EST3_TIME_RELATED_INDIRECT_METHODS.includes(row.calculation_method)
      ),
    [indirectQ.data]
  );

  const timeRelatedTotal = useMemo(
    () => timeRelatedLines.reduce((acc, row: any) => acc + (Number(row.calculated_amount) || 0), 0),
    [timeRelatedLines]
  );

  const durationMonths =
    durationQ.data?.overall_duration_months ??
    durationQ.data?.estimate_assumed_months ??
    totalQ.data?.duration_summary?.overall_months;

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <EpcEstimateProjectCostStrip ctx={ctx} activeLeaf="duration" />

      <Row gutter={16}>
        <Col xs={24} sm={8}>
          <Statistic
            title="Overall duration (mo)"
            value={durationMonths != null ? Number(durationMonths) : '—'}
          />
        </Col>
        <Col xs={24} sm={8}>
          <Statistic
            title="Time-related indirect lines"
            value={timeRelatedLines.length}
          />
        </Col>
        <Col xs={24} sm={8}>
          <Statistic
            title="Time-related indirect total"
            value={timeRelatedTotal}
            formatter={(v) => formatMoney(v, { display: 'plain', maximumFractionDigits: 0 })}
          />
        </Col>
      </Row>

      <Alert
        type="info"
        showIcon
        message="Duration drives monthly / resource×duration indirect methods. Edit duration below; manage all indirects on the Indirects leaf."
      />

      <Wave5DurationPanel ctx={ctx} />

      <Card size="small" title="Duration-linked indirect lines">
        <Table
          size="small"
          rowKey="id"
          loading={indirectQ.isLoading}
          dataSource={timeRelatedLines}
          pagination={timeRelatedLines.length > 20 ? { pageSize: 20 } : false}
          locale={{ emptyText: 'No MONTHLY×DURATION or RESOURCE×DURATION indirect lines yet.' }}
          columns={[
            { title: 'Category', dataIndex: 'category', width: 140 },
            { title: 'Description', dataIndex: 'description', ellipsis: true },
            {
              title: 'Method',
              dataIndex: 'calculation_method',
              width: 160,
              render: (v) => <Tag>{v}</Tag>,
            },
            {
              title: 'Amount',
              dataIndex: 'calculated_amount',
              width: 110,
              render: (v) => formatMoney(v, { display: 'plain', maximumFractionDigits: 2 }),
            },
          ]}
        />
        <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
          Variance{' '}
          {totalQ.data?.duration_summary?.variance_months ??
            durationQ.data?.variance_months ??
            '—'}{' '}
          months vs client required duration.
        </Text>
      </Card>
    </Space>
  );
};
